import { useMergedRefs } from '@base-ui/utils/useMergedRefs';
import {
    Children,
    useContext,
    useLayoutEffect,
    useRef,
    useState,
    type CSSProperties,
    type ComponentPropsWithRef,
    type ReactElement,
} from 'react';
import { cn } from '../util/classes.ts';
import { PushStackContext, PushStackIndexContext, type PushStackContextValue } from './push-stack-context.ts';

function usePushStack(): PushStackContextValue {
    const context = useContext(PushStackContext);

    if (!context) {
        throw new Error('PushStackLevel must be rendered inside <PushStack>');
    }

    return context;
}

export type PushStackProps = ComponentPropsWithRef<'div'> & {
    /** Which level is on screen, as an index into the `PushStackLevel` children. */
    activeIndex: number;
};

/** React's `CSSProperties` has no room for custom properties. */
type PushStackStyle = CSSProperties & { '--push-stack-height'?: string };

/**
 * Push-down navigation inside one surface: a menu that drills into a sub-menu
 * without opening a second popover. Levels are children in depth order and
 * `activeIndex` picks one; the box morphs to whichever is on screen.
 *
 * Condition a level's *content*, never the level — an unmounted level has no
 * pose to animate from, so it appears instead of sliding in.
 *
 * @example
 * <PushStack activeIndex={section === undefined ? 0 : 1}>
 *     <PushStackLevel>
 *         <SectionRows onOpen={setSection} />
 *     </PushStackLevel>
 *     <PushStackLevel>{section && <SectionDetail section={section} />}</PushStackLevel>
 * </PushStack>
 */
export function PushStack({
    activeIndex,
    className,
    style,
    children,
    ...props
}: PushStackProps): ReactElement {
    // JS measures and hands the number to CSS, which animates it — the split
    // base-ui uses for `--collapsible-panel-height`.
    const [height, setHeight] = useState<number>();

    const heightStyle: PushStackStyle | undefined =
        height === undefined ? style : { ...style, '--push-stack-height': `${height}px` };

    return (
        <PushStackContext.Provider value={{ activeIndex, onActiveHeight: setHeight }}>
            <div
                data-slot="push-stack"
                className={cn(
                    'relative h-(--push-stack-height) overflow-hidden [--push-stack-height:auto]',
                    'transition-[height] duration-state ease-decelerate',
                    className,
                )}
                style={heightStyle}
                {...props}
            >
                {Children.map(children, (child, index) => (
                    <PushStackIndexContext.Provider value={index}>{child}</PushStackIndexContext.Provider>
                ))}
            </div>
        </PushStackContext.Provider>
    );
}

export type PushStackLevelProps = ComponentPropsWithRef<'div'>;

/**
 * One level of a {@link PushStack}: in flow while active, otherwise an inert
 * overlay parked on the side it returns from, ready to transition back.
 */
export function PushStackLevel({ className, ref, ...props }: PushStackLevelProps): ReactElement {
    const { activeIndex, onActiveHeight } = usePushStack();
    const index = useContext(PushStackIndexContext);
    const levelRef = useRef<HTMLDivElement>(null);
    const mergedRef = useMergedRefs(ref, levelRef);
    const isActive = index === activeIndex;

    useLayoutEffect(() => {
        const element = levelRef.current;

        if (!isActive || !element) {
            return;
        }

        const measure = (): void => {
            onActiveHeight(element.offsetHeight);
        };

        measure();
        // Deps only cover level changes; without this a level that grows in
        // place — an async row arriving, a reflow — stays clipped at its old height.
        const observer = new ResizeObserver(measure);
        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [isActive, onActiveHeight]);

    return (
        <div
            ref={mergedRef}
            inert={!isActive}
            data-slot="push-stack-level"
            data-state={isActive ? 'active' : 'inactive'}
            data-side={index > activeIndex ? 'right' : 'left'}
            className={cn(
                'transition-[translate,opacity] duration-state ease-decelerate',
                'data-[state=active]:relative data-[state=active]:translate-x-0 data-[state=active]:opacity-100',
                'data-[state=inactive]:pointer-events-none data-[state=inactive]:absolute',
                'data-[state=inactive]:inset-x-0 data-[state=inactive]:top-0 data-[state=inactive]:opacity-0',
                'data-[state=inactive]:data-[side=left]:-translate-x-2',
                'data-[state=inactive]:data-[side=right]:translate-x-2',
                className,
            )}
            {...props}
        />
    );
}
